// GradientFlow shader - flowing gradients with rotation and wave distortion
const frag = `
precision mediump float;

uniform vec2 u_resolution;
uniform float u_time;
uniform float u_speed;
uniform float u_seed;

uniform float u_frequency;    // Wave frequency (1.0 - 20.0 recommended)
uniform float u_amplitude;    // Wave amplitude (0.0 - 50.0 recommended)
uniform float u_rotation;     // Rotation intensity (0.0 - 1.0 recommended)
uniform float u_blend_pos;    // Blend position (-1.0 - 1.0 recommended)

uniform vec3 u_color_0;
uniform vec3 u_color_1;
uniform vec3 u_color_2;
uniform vec3 u_color_3;
uniform vec3 u_color_4;
uniform vec3 u_color_5;

varying vec2 vUv;

#define S(a,b,t) smoothstep(a,b,t)

mat2 Rot(float a) {
    float s = sin(a);
    float c = cos(a);
    return mat2(c, -s, s, c);
}

// Hash function by inigo quilez
vec2 hash(vec2 p) {
    p = vec2(dot(p, vec2(2127.1, 81.17)), dot(p, vec2(1269.5, 283.37)));
    return fract(sin(p) * 43758.5453);
}

float noise(in vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    
    vec2 u = f * f * (3.0 - 2.0 * f);

    float n = mix(mix(dot(-1.0 + 2.0 * hash(i + vec2(0.0, 0.0)), f - vec2(0.0, 0.0)), 
                      dot(-1.0 + 2.0 * hash(i + vec2(1.0, 0.0)), f - vec2(1.0, 0.0)), u.x),
                  mix(dot(-1.0 + 2.0 * hash(i + vec2(0.0, 1.0)), f - vec2(0.0, 1.0)), 
                      dot(-1.0 + 2.0 * hash(i + vec2(1.0, 1.0)), f - vec2(1.0, 1.0)), u.x), u.y);
    return 0.5 + 0.5 * n;
}

// Color lookup function similar to wavy-waves
vec3 lut(float x) {
    if (x < 0.0 || x > 1.0) return u_color_0;

    float P1 = abs(x - 0.0);
    float P2 = abs(x - 0.167);
    float P3 = abs(x - 0.334);
    float P4 = abs(x - 0.500);
    float P5 = abs(x - 0.667);
    float P6 = abs(x - 0.833);
    float P7 = abs(x - 1.0);

    float expand = 6.0; // Fixed expand for consistent blending
    float R1 = smoothstep(1.0, 0.0, P1 * expand);
    float R2 = smoothstep(1.0, 0.0, P2 * expand);
    float R3 = smoothstep(1.0, 0.0, P3 * expand);
    float R4 = smoothstep(1.0, 0.0, P4 * expand);
    float R5 = smoothstep(1.0, 0.0, P5 * expand);
    float R6 = smoothstep(1.0, 0.0, P6 * expand);
    float R7 = smoothstep(1.0, 0.0, P7 * expand);

    vec3 color = u_color_0 * R1 +
                 u_color_1 * R2 +
                 u_color_2 * R3 +
                 u_color_3 * R4 +
                 u_color_4 * R5 +
                 u_color_5 * R6 +
                 u_color_0 * R7;
    return color;
}

void main() {
    vec2 uv = gl_FragCoord.xy / u_resolution.xy;
    float ratio = u_resolution.x / u_resolution.y;

    vec2 tuv = uv;
    tuv -= 0.5;

    // Rotate with noise and seed influence
    float degree = noise(vec2(u_time * u_speed * 0.1 + u_seed * 0.1, tuv.x * tuv.y + u_seed * 0.01));

    tuv.y *= 1.0 / ratio;
    tuv *= Rot(radians((degree - 0.5) * 720.0 * u_rotation + 180.0));
    tuv.y *= ratio;

    // Wave warp with sin
    float speed = u_time * u_speed * 2.0;
    tuv.x += sin(tuv.y * u_frequency + speed) / u_amplitude;
    tuv.y += sin(tuv.x * u_frequency * 1.5 + speed) / (u_amplitude * 0.5);
    
    // Create gradient layers using color palette
    vec3 layer1 = mix(u_color_0, u_color_1, S(-0.3, 0.2, (tuv * Rot(radians(-5.0))).x));
    vec3 layer2 = mix(u_color_2, u_color_3, S(-0.3, 0.2, (tuv * Rot(radians(-5.0))).x));
    
    // Additional layers for more complex blending with 6-color palette
    vec3 layer3 = mix(u_color_4, u_color_5, S(-0.3, 0.2, (tuv * Rot(radians(5.0))).x));
    
    // Blend layers based on position and user control
    vec3 finalComp = mix(layer1, layer2, S(0.5, u_blend_pos, tuv.y));
    finalComp = mix(finalComp, layer3, S(-0.5, u_blend_pos * 0.5, tuv.x));
    
    vec3 col = finalComp;
    
    // Gamma correction
    col = clamp(col, 0.0, 1.0);
    col = sqrt(col);
    
    gl_FragColor = vec4(col, 1.0);
}
`;

const shader = {
  name: 'GradientFlow',
  shader: {
    frag,
  },
  schema: [
    {
      key: 'colors',
      type: 'color-palette',
      label: 'Colors',
      min: 2,
      max: 6,
      default: ['#f5af19', '#f12711', '#c31432', '#240b36', '#4ecdc4', '#44a08d'],
    },
    {
      key: 'u_frequency',
      type: 'number',
      label: 'Frequency',
      min: 1,
      max: 20,
      step: 0.5,
      default: 5,
    },
    {
      key: 'u_amplitude',
      type: 'number',
      label: 'Amplitude',
      min: 0,
      max: 50,
      step: 1,
      default: 30,
    },
    {
      key: 'u_rotation',
      type: 'number',
      label: 'Rotation',
      min: 0,
      max: 1,
      step: 0.05,
      default: 1,
    },
    {
      key: 'u_blend_pos',
      type: 'number',
      label: 'Blend Position',
      min: -1,
      max: 1,
      step: 0.1,
      default: -0.3,
    },
    { key: 'speed', type: 'number', label: 'Speed', min: 0, max: 10, step: 0.1, default: 1 },
    { key: 'animated', type: 'boolean', label: 'Animated', default: true },
  ],
  examples: [
    'dynamic-shader(GradientFlow, colors=#ff6b6b,#4ecdc4,#45b7d1,#96ceb4,#feca57,#ff9ff3; u_frequency=7; u_amplitude=25; u_rotation=0.8; u_blend_pos=-0.2; speed=1.2; animated=true)',
    'dynamic-shader(GradientFlow, colors=#667eea,#764ba2,#f093fb,#f5576c,#43e97b,#38f9d7; u_frequency=4; u_amplitude=35; u_rotation=0.6; u_blend_pos=0.3; speed=0.9; animated=true)',
    'dynamic-shader(GradientFlow, colors=#1a2a6c,#b21f1f,#fdbb2d,#ee5a24,#0abde3,#006ba6; u_frequency=9; u_amplitude=20; u_rotation=1.0; u_blend_pos=-0.4; speed=0.7; animated=true)',
    'dynamic-shader(GradientFlow, colors=#a8ff78,#78ffd6,#2af598,#18dcff,#7209b7,#560bad; u_frequency=6; u_amplitude=28; u_rotation=0.4; u_blend_pos=0.1; speed=1.5; animated=true)',
    'dynamic-shader(GradientFlow, colors=#ff9a9e,#fecfef,#fbc2eb,#a6c1ee,#c7ceea,#b8b5ff; u_frequency=5; u_amplitude=32; u_rotation=0.7; u_blend_pos=-0.1; speed=0.6; animated=false)',
    'dynamic-shader(GradientFlow, colors=#fc466b,#3f5efb,#8b5cf6,#06ffa5,#f093fb,#6c5ce7; u_frequency=11; u_amplitude=18; u_rotation=0.9; u_blend_pos=0.4; speed=1.8; animated=true)',
    'dynamic-shader(GradientFlow, colors=#ffd89b,#19547b,#70e1f5,#ffd194,#c471ed,#12c2e9; u_frequency=3; u_amplitude=42; u_rotation=0.3; u_blend_pos=-0.6; speed=1.0; animated=true)',
    'dynamic-shader(GradientFlow, colors=#f5af19,#f12711,#74b9ff,#0984e3,#6c5ce7,#a29bfe; u_frequency=8; u_amplitude=24; u_rotation=0.5; u_blend_pos=0.2; speed=1.3; animated=true)',
  ],
};

export default shader;
